import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CtaSection from '../../components/feature/CtaSection';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function Contact() {
  useSEO({
    title: '診療予約・お問い合わせ｜おかやま小児科クリニック｜岡山市北区',
    description:
      '岡山市北区のおかやま小児科クリニックへの診療予約・お問い合わせ。Web予約フォーム、電話086-012-3456、FAX086-012-3457。診療時間9:00-18:00、日曜・祝日休診。アクセスマップ掲載。',
    keywords:
      '岡山市,小児科,予約,お問い合わせ,電話,アクセス,地図,診療時間,北区',
    canonicalPath: '/contact',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          '診療予約・お問い合わせ',
          'Web予約フォーム、電話・FAX番号、アクセスマップ',
          '/contact'
        ),
        {
          '@type': 'ContactPage',
          name: '診療予約・お問い合わせ',
          description: 'おかやま小児科クリニックへのお問い合わせ方法',
          url: `${import.meta.env.VITE_SITE_URL || ''}/contact`,
        },
      ],
    },
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    childName: '',
    childAge: '',
    appointmentType: '一般診療',
    preferredDate: '',
    preferredTime: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d6ba98tbd17lru29magg',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          childName: '',
          childAge: '',
          appointmentType: '一般診療',
          preferredDate: '',
          preferredTime: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[25vh] sm:h-[30vh] flex items-center justify-center overflow-hidden mt-20 bg-gradient-to-r from-[#F48FB1] via-[#D4A0D0] to-[#A78BFA]">
        <div className="relative z-10 text-center text-white px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            診療予約・お問い合わせ
          </h2>
          <p className="text-base sm:text-lg md:text-xl">お気軽にご連絡ください</p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#FFFDE7]/60 via-[#FCE4EC]/40 to-[#EDE7F6]/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#F48FB1] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-phone-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">
                お電話でのご予約
              </h3>
              <a
                href="tel:0860123456"
                className="text-xl sm:text-2xl font-bold text-[#F48FB1] hover:text-[#EC407A] transition-colors whitespace-nowrap"
              >
                086-012-3456
              </a>
              <p className="text-sm sm:text-base text-gray-600 mt-3 sm:mt-4">
                受付時間：9:00〜18:00<br />
                （日曜・祝日を除く）
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#FFD54F] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-mail-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">
                FAXでのお問い合わせ
              </h3>
              <p className="text-xl sm:text-2xl font-bold text-gray-800 whitespace-nowrap">
                086-012-3457
              </p>
              <p className="text-sm sm:text-base text-gray-600 mt-3 sm:mt-4">
                24時間受付<br />
                （返信は診療時間内）
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#A78BFA] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-map-pin-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">
                所在地
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                〒700-0000<br />
                岡山市北区○○町1-2-3
              </p>
              <p className="text-sm sm:text-base text-gray-600 mt-3 sm:mt-4">
                駐車場10台完備
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Web予約フォーム
            </h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">
              下記フォームよりご予約・お問い合わせください
            </p>
          </div>

          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-gradient-to-br from-[#FCE4EC]/50 to-[#FFFDE7]/40 rounded-3xl shadow-xl p-6 sm:p-8 md:p-12"
          >
            <div className="space-y-6 sm:space-y-8">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3"
                >
                  保護者様のお名前{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-full border-2 border-gray-200 focus:border-[#F48FB1] focus:outline-none text-sm sm:text-base transition-colors"
                  placeholder="山田 太郎"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3"
                >
                  メールアドレス{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-full border-2 border-gray-200 focus:border-[#F48FB1] focus:outline-none text-sm sm:text-base transition-colors"
                  placeholder="example@email.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3"
                >
                  電話番号{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-full border-2 border-gray-200 focus:border-[#F48FB1] focus:outline-none text-sm sm:text-base transition-colors"
                  placeholder="090-1234-5678"
                />
              </div>

              {/* Child Name */}
              <div>
                <label
                  htmlFor="childName"
                  className="block text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3"
                >
                  お子様のお名前{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="childName"
                  name="childName"
                  value={formData.childName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-full border-2 border-gray-200 focus:border-[#F48FB1] focus:outline-none text-sm sm:text-base transition-colors"
                  placeholder="山田 花子"
                />
              </div>

              {/* Child Age */}
              <div>
                <label
                  htmlFor="childAge"
                  className="block text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3"
                >
                  お子様の年齢{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="childAge"
                  name="childAge"
                  value={formData.childAge}
                  onChange={handleChange}
                  required
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-full border-2 border-gray-200 focus:border-[#F48FB1] focus:outline-none text-sm sm:text-base transition-colors"
                  placeholder="例：3歳6ヶ月"
                />
              </div>

              {/* Appointment Type */}
              <div>
                <label
                  htmlFor="appointmentType"
                  className="block text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3"
                >
                  診療内容{' '}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="appointmentType"
                  name="appointmentType"
                  value={formData.appointmentType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-full border-2 border-gray-200 focus:border-[#F48FB1] focus:outline-none text-sm sm:text-base transition-colors cursor-pointer"
                >
                  <option value="一般診療">一般診療</option>
                  <option value="予防接種">予防接種</option>
                  <option value="乳幼児健診">乳幼児健診</option>
                  <option value="アレルギー相談">アレルギー相談</option>
                  <option value="育児相談">育児相談</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              {/* Preferred Date */}
              <div>
                <label
                  htmlFor="preferredDate"
                  className="block text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3"
                >
                  ご希望日
                </label>
                <input
                  type="date"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleChange}
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-full border-2 border-gray-200 focus:border-[#F48FB1] focus:outline-none text-sm sm:text-base transition-colors"
                />
              </div>

              {/* Preferred Time */}
              <div>
                <label
                  htmlFor="preferredTime"
                  className="block text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3"
                >
                  ご希望時間帯
                </label>
                <select
                  id="preferredTime"
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleChange}
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-full border-2 border-gray-200 focus:border-[#F48FB1] focus:outline-none text-sm sm:text-base transition-colors cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="午前（9:00-12:00）">午前（9:00-12:00）</option>
                  <option value="午後（14:00-18:00）">午後（14:00-18:00）</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3"
                >
                  症状・ご相談内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 rounded-3xl border-2 border-gray-200 focus:border-[#F48FB1] focus:outline-none text-sm sm:text-base transition-colors resize-none"
                  placeholder="症状やご相談内容をご記入ください（500文字以内）"
                ></textarea>
                <p className="text-xs sm:text-sm text-gray-500 mt-2 text-right">
                  {formData.message.length}/500文字
                </p>
              </div>

              {/* Submit Button */}
              <div className="text-center pt-4 sm:pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#F48FB1] hover:bg-[#EC407A] disabled:bg-gray-400 text-white px-12 sm:px-16 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '送信中...' : '予約・お問い合わせを送信'}
                </button>
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-500 rounded-3xl p-4 sm:p-6 text-center">
                  <i className="ri-checkbox-circle-line text-3xl sm:text-4xl text-green-500 mb-2"></i>
                  <p className="text-sm sm:text-base text-green-700 font-bold">
                    送信が完了しました。<br />
                    ご予約内容を確認の上、折り返しご連絡させていただきます。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-500 rounded-3xl p-4 sm:p-6 text-center">
                  <i className="ri-error-warning-line text-3xl sm:text-4xl text-red-500 mb-2"></i>
                  <p className="text-sm sm:text-base text-red-700 font-bold">
                    送信に失敗しました。<br />
                    お手数ですが、お電話にてご連絡ください。
                  </p>
                </div>
              )}
            </div>
          </form>

          <div className="mt-8 sm:mt-12 text-center">
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
              ※ご予約は仮予約となります。当院からの確認連絡をもって予約確定となります。<br />
              ※お急ぎの場合は、お電話にてご連絡ください。
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
